/* eslint-disable bsky-internal/avoid-unwrapped-text */
import {useState} from 'react'
import {useQuery} from '@tanstack/react-query'

import {ROUTES} from '#/lib/routes/constants.ts'
import {useAgent} from '#/state/session'
import {useIsMobileWeb} from '#/alf'
import {Button, ButtonText} from '#/components/Button'
import {AccentLink, BodyText, Heading} from '#/wsocial/components/primitives'
import {useSemanticTokens} from '#/wsocial/service/design-system'
import {
  AppStoreBadge,
  AppStoreBadgesRow,
  GooglePlayBadge,
} from './AppStoreBadges'
import {IllustrationAge18} from './illustrations'
import {
  BackButton,
  Checkbox,
  Divider,
  ErrorMsg,
  IllustrationArea,
  MobileTopNav,
  PageShell,
} from './primitives'
import {openWIdentityDeepLink, useQrDeepLink} from './useQrDeepLink'

export function StepInstallWid({
  onBack,
  onSkip,
  onNext,
  geoRestricted = false,
}: {
  onBack: () => void
  onSkip?: () => void
  onNext: () => void
  geoRestricted?: boolean
}) {
  const isMobileWeb = useIsMobileWeb()
  const tokens = useSemanticTokens()
  const agent = useAgent()
  const [checked, setChecked] = useState(false)

  const {data: session} = useQuery({
    queryKey: ['wid-setup-mobile'],
    queryFn: async ({signal}) =>
      agent.quicklogin.allocateWidForAccount({signal}),
    enabled: isMobileWeb,
  })
  const deepLink = useQrDeepLink(isMobileWeb ? session?.qrCodeUrl : undefined)

  return (
    <PageShell>
      {isMobileWeb && <MobileTopNav onBack={onBack} />}
      {geoRestricted && (
        <IllustrationArea>
          <IllustrationAge18 />
        </IllustrationArea>
      )}

      {isMobileWeb ? (
        /* Mobile layout — left-aligned, Google Play badge first */
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
            padding: '24px 16px',
          }}>
          <Heading>
            {geoRestricted
              ? 'Verify your age — Install W Identity'
              : 'Install W Identity'}
          </Heading>
          <BodyText>
            {geoRestricted
              ? "It's required by law in your region. Find it on the App Store or Google Play."
              : 'Install W Identity, then set up your ID inside the app.'}
          </BodyText>
          <div
            style={{
              display: 'flex',
              gap: 8,
              padding: '16px 0',
              justifyContent: 'center',
            }}>
            <GooglePlayBadge />
            <AppStoreBadge />
          </div>
        </div>
      ) : (
        /* Desktop layout — centered, App Store badge first */
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
            padding: '24px 16px',
            textAlign: 'center',
          }}>
          <Heading>
            {geoRestricted
              ? 'Verify your age — Install W Identity'
              : 'Install W Identity'}
          </Heading>
          <BodyText>
            {geoRestricted
              ? "It's required by law in your region. Find it on the App Store or Google Play."
              : 'You can find it on App Store and Google Play.'}
          </BodyText>
          <AppStoreBadgesRow />
        </div>
      )}

      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 20,
          padding: '16px 16px',
        }}>
        {isMobileWeb ? (
          <div style={{display: 'flex', flexDirection: 'column', gap: 10}}>
            <Checkbox
              checked={checked}
              onChange={setChecked}
              label="I have installed W Identity"
            />
            {!checked && (
              <ErrorMsg msg="You need to install W Identity to continue." />
            )}
            <Button
              label="I have installed W Identity"
              color="primary"
              size="large"
              testID="continueBtn"
              disabled={!checked || !deepLink}
              onPress={() => openWIdentityDeepLink(deepLink)}
              style={{width: '100%'}}>
              <ButtonText>I have installed W Identity</ButtonText>
            </Button>
            {onSkip && (
              <Button
                label="Explore W without verifying"
                color="secondary"
                size="large"
                testID="remindLaterBtn"
                onPress={onSkip}
                style={{width: '100%'}}>
                <ButtonText>Explore W without verifying</ButtonText>
              </Button>
            )}
          </div>
        ) : (
          <div style={{display: 'flex', alignItems: 'center', gap: 10}}>
            <BackButton onPress={onBack} />
            <div
              style={{
                flex: 1,
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 10,
              }}>
              {onSkip && (
                <Button
                  label="Explore W without verifying"
                  color="secondary"
                  size="large"
                  testID="remindLaterBtn"
                  onPress={onSkip}>
                  <ButtonText>Explore W without verifying</ButtonText>
                </Button>
              )}
              <Button
                label="I have installed W Identity"
                color="primary"
                size="large"
                testID="continueBtn"
                onPress={onNext}>
                <ButtonText>I have installed W Identity</ButtonText>
              </Button>
            </div>
          </div>
        )}
        <Divider />
        <p
          style={{
            fontSize: 13,
            lineHeight: '17px',
            color: tokens.onboarding.textLow,
            margin: 0,
            textAlign: isMobileWeb ? 'center' : 'left',
          }}>
          Verify anonymously or with your real name. W Identity is fully
          decentralised, ensuring your data stays on your device. Read our{' '}
          <AccentLink
            style={{color: tokens.onboarding.textMedium}}
            href={ROUTES.PRIVACY_POLICY}>
            Privacy policy
          </AccentLink>
          .
        </p>
      </div>
    </PageShell>
  )
}
